import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button as HeadlessButton } from "@headlessui/react";
import { clsx } from "clsx";
import React from "react";
import { Link } from "./Link";
const styles = {
    base: [
        // Base
        "relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border text-base/6 font-semibold",
        // Sizing
        "px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing.3)-1px)] sm:py-[calc(theme(spacing[1.5])-1px)] sm:text-sm/6",
        // Focus
        "focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500",
        // Disabled
        "data-[disabled]:opacity-50",
        // Icon
        "[&>[data-slot=icon]]:-mx-0.5 [&>[data-slot=icon]]:my-0.5 [&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-[--btn-icon] [&>[data-slot=icon]]:sm:my-1 [&>[data-slot=icon]]:sm:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-[hover]:[--btn-icon:ButtonText]"
    ],
    solid: [
        // Optical border, implemented as the button background to avoid corner artifacts
        "border-transparent bg-[--btn-border]",
        // Dark mode: border is rendered on `after` so background is set to button background
        "dark:bg-[--btn-bg]",
        // Button background, implemented as foreground layer to stack on top of pseudo-border layer
        "before:absolute before:inset-0 before:-z-10 before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-[--btn-bg]",
        // Drop shadow, applied to the inset `before` layer so it blends with the border
        "before:shadow",
        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
        "dark:before:hidden",
        // Dark mode: Subtle white outline is applied using a border
        "dark:border-white/5",
        // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
        "after:absolute after:inset-0 after:-z-10 after:rounded-[calc(theme(borderRadius.lg)-1px)]",
        // Inner highlight shadow
        "after:shadow-[shadow:inset_0_1px_theme(colors.white/15%)]",
        // White overlay on hover
        "after:data-[active]:bg-[--btn-hover-overlay] after:data-[hover]:bg-[--btn-hover-overlay]",
        // Dark mode: `after` layer expands to cover entire button
        "dark:after:-inset-px dark:after:rounded-lg",
        // Disabled
        "before:data-[disabled]:shadow-none after:data-[disabled]:shadow-none"
    ],
    outline: [
        // Base
        "border-zinc-950/10 text-zinc-950 data-[active]:bg-zinc-950/[2.5%] data-[hover]:bg-zinc-950/[2.5%]",
        // Dark mode
        "dark:border-white/15 dark:text-white dark:[--btn-bg:transparent] dark:data-[active]:bg-white/5 dark:data-[hover]:bg-white/5",
        // Icon
        "[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.700)] data-[hover]:[--btn-icon:theme(colors.zinc.700)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]"
    ],
    plain: [
        // Base
        "border-transparent text-zinc-950 data-[active]:bg-zinc-950/5 data-[hover]:bg-zinc-950/5",
        // Dark mode
        "dark:text-white dark:data-[active]:bg-white/10 dark:data-[hover]:bg-white/10",
        // Icon
        "[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.700)] data-[hover]:[--btn-icon:theme(colors.zinc.700)] dark:[--btn-icon:theme(colors.zinc.500)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]"
    ],
    flat: [
        // Reset the base styles and apply simple flat button styling
        "!relative !isolate inline-flex items-center justify-center gap-x-2 !rounded-none !border-transparent !text-base !font-aeonik !py-2 !px-8 disabled:opacity-20 !text-navy !bg-transparent",
        "hover:bg-navy/5 active:bg-navy/10 focus:outline-none",
        // Reset all the complex pseudo-elements
        "!before:hidden !after:hidden"
    ],
    default: [
        // Reset the base styles and apply simple default button styling
        "!relative !isolate inline-flex items-center justify-center gap-x-2 !rounded !border-transparent !text-base !font-aeonik !py-2 !px-8 disabled:opacity-20 !bg-navy-90 !text-navy-5",
        "hover:bg-navy-90/90 active:bg-navy-90/80 focus:outline-none",
        // Reset all the complex pseudo-elements
        "!before:hidden !after:hidden"
    ],
    simple: {
        base: "font-aeonik py-2 px-8 disabled:opacity-20 transition-colors transition-opacity duration-150 ease-in-out",
        default: "bg-navy-90 rounded text-navy-5",
        flat: "text-navy",
        colors: {
            "navy-90": "bg-navy-90 rounded text-navy-5 hover:bg-navy-90/90 disabled:hover:bg-navy-90",
            red: "bg-red-600 rounded text-white hover:bg-red-500 disabled:hover:bg-red-600",
            green: "bg-green-600 rounded text-white hover:bg-green-500 disabled:hover:bg-green-600",
            blue: "bg-blue-600 rounded text-white hover:bg-blue-500 disabled:hover:bg-blue-600",
            gray: "bg-gray-600 rounded text-white hover:bg-gray-500 disabled:hover:bg-gray-600",
            "navy-light": "bg-navy-5 rounded text-navy border border-navy-20 hover:bg-navy-10 disabled:hover:bg-navy-5"
        }
    },
    sharp: [
        // Base sharp styles with no border radius (sharp edges)
        "relative isolate inline-flex items-center justify-center gap-x-2 border text-base/6 font-semibold",
        "rounded-none",
        // Sizing
        "px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing.3)-1px)] sm:py-[calc(theme(spacing[1.5])-1px)] sm:text-sm/6",
        // Focus
        "focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500",
        // Disabled
        "data-[disabled]:opacity-50",
        // Icon
        "[&>[data-slot=icon]]:-mx-0.5 [&>[data-slot=icon]]:my-0.5 [&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-[--btn-icon] [&>[data-slot=icon]]:sm:my-1 [&>[data-slot=icon]]:sm:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-[hover]:[--btn-icon:ButtonText]",
        // Optical border, implemented as the button background to avoid corner artifacts
        "border-transparent bg-[--btn-border]",
        // Dark mode: border is rendered on `after` so background is set to button background
        "dark:bg-[--btn-bg]",
        // Button background, implemented as foreground layer to stack on top of pseudo-border layer
        "before:absolute before:inset-0 before:-z-10 before:rounded-none before:bg-[--btn-bg]",
        // Drop shadow, applied to the inset `before` layer so it blends with the border
        "before:shadow",
        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
        "dark:before:hidden",
        // Dark mode: Subtle white outline is applied using a border
        "dark:border-white/5",
        // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
        "after:absolute after:inset-0 after:-z-10 after:rounded-none",
        // Inner highlight shadow
        "after:shadow-[shadow:inset_0_1px_theme(colors.white/15%)]",
        // White overlay on hover
        "after:data-[active]:bg-[--btn-hover-overlay] after:data-[hover]:bg-[--btn-hover-overlay]",
        // Dark mode: `after` layer expands to cover entire button
        "dark:after:-inset-px dark:after:rounded-none",
        // Disabled
        "before:data-[disabled]:shadow-none after:data-[disabled]:shadow-none"
    ],
    colors: {
        "dark/zinc": [
            "text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]",
            "dark:text-white dark:[--btn-bg:theme(colors.zinc.600)] dark:[--btn-hover-overlay:theme(colors.white/5%)]",
            "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]"
        ],
        light: [
            "text-zinc-950 [--btn-bg:white] [--btn-border:theme(colors.zinc.950/10%)] [--btn-hover-overlay:theme(colors.zinc.950/2.5%)] data-[active]:[--btn-border:theme(colors.zinc.950/15%)] data-[hover]:[--btn-border:theme(colors.zinc.950/15%)]",
            "dark:text-white dark:[--btn-hover-overlay:theme(colors.white/5%)] dark:[--btn-bg:theme(colors.zinc.800)]",
            "[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.700)] data-[hover]:[--btn-icon:theme(colors.zinc.700)] dark:[--btn-icon:theme(colors.zinc.500)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]"
        ],
        "dark/white": [
            "text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]",
            "dark:text-zinc-950 dark:[--btn-bg:white] dark:[--btn-hover-overlay:theme(colors.zinc.950/5%)]",
            "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)] dark:[--btn-icon:theme(colors.zinc.500)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]"
        ],
        dark: [
            "text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]",
            "dark:[--btn-hover-overlay:theme(colors.white/5%)] dark:[--btn-bg:theme(colors.zinc.800)]",
            "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]"
        ],
        white: [
            "text-zinc-950 [--btn-bg:white] [--btn-border:theme(colors.zinc.950/10%)] [--btn-hover-overlay:theme(colors.zinc.950/2.5%)] data-[active]:[--btn-border:theme(colors.zinc.950/15%)] data-[hover]:[--btn-border:theme(colors.zinc.950/15%)]",
            "dark:[--btn-hover-overlay:theme(colors.zinc.950/5%)]",
            "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.500)] data-[hover]:[--btn-icon:theme(colors.zinc.500)]"
        ],
        zinc: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.zinc.600)] [--btn-border:theme(colors.zinc.700/90%)]",
            "dark:[--btn-hover-overlay:theme(colors.white/5%)]",
            "[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]"
        ],
        indigo: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.indigo.500)] [--btn-border:theme(colors.indigo.600/90%)]",
            "[--btn-icon:theme(colors.indigo.300)] data-[active]:[--btn-icon:theme(colors.indigo.200)] data-[hover]:[--btn-icon:theme(colors.indigo.200)]"
        ],
        cyan: [
            "text-cyan-950 [--btn-bg:theme(colors.cyan.300)] [--btn-border:theme(colors.cyan.400/80%)] [--btn-hover-overlay:theme(colors.white/25%)]",
            "[--btn-icon:theme(colors.cyan.500)]"
        ],
        red: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.red.600)] [--btn-border:theme(colors.red.700/90%)]",
            "[--btn-icon:theme(colors.red.300)] data-[active]:[--btn-icon:theme(colors.red.200)] data-[hover]:[--btn-icon:theme(colors.red.200)]"
        ],
        orange: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.orange.500)] [--btn-border:theme(colors.orange.600/90%)]",
            "[--btn-icon:theme(colors.orange.300)] data-[active]:[--btn-icon:theme(colors.orange.200)] data-[hover]:[--btn-icon:theme(colors.orange.200)]"
        ],
        amber: [
            "text-amber-950 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.amber.400)] [--btn-border:theme(colors.amber.500/80%)]",
            "[--btn-icon:theme(colors.amber.600)]"
        ],
        yellow: [
            "text-yellow-950 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.yellow.300)] [--btn-border:theme(colors.yellow.400/80%)]",
            "[--btn-icon:theme(colors.yellow.600)] data-[active]:[--btn-icon:theme(colors.yellow.700)] data-[hover]:[--btn-icon:theme(colors.yellow.700)]"
        ],
        lime: [
            "text-lime-950 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.lime.300)] [--btn-border:theme(colors.lime.400/80%)]",
            "[--btn-icon:theme(colors.lime.600)] data-[active]:[--btn-icon:theme(colors.lime.700)] data-[hover]:[--btn-icon:theme(colors.lime.700)]"
        ],
        green: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.green.600)] [--btn-border:theme(colors.green.700/90%)]",
            "[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]"
        ],
        emerald: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.emerald.600)] [--btn-border:theme(colors.emerald.700/90%)]",
            "[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]"
        ],
        teal: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.teal.600)] [--btn-border:theme(colors.teal.700/90%)]",
            "[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]"
        ],
        sky: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.sky.500)] [--btn-border:theme(colors.sky.600/80%)]",
            "[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]"
        ],
        blue: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.blue.600)] [--btn-border:theme(colors.blue.700/90%)]",
            "[--btn-icon:theme(colors.blue.400)] data-[active]:[--btn-icon:theme(colors.blue.300)] data-[hover]:[--btn-icon:theme(colors.blue.300)]"
        ],
        violet: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.violet.500)] [--btn-border:theme(colors.violet.600/90%)]",
            "[--btn-icon:theme(colors.violet.300)] data-[active]:[--btn-icon:theme(colors.violet.200)] data-[hover]:[--btn-icon:theme(colors.violet.200)]"
        ],
        purple: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.purple.500)] [--btn-border:theme(colors.purple.600/90%)]",
            "[--btn-icon:theme(colors.purple.300)] data-[active]:[--btn-icon:theme(colors.purple.200)] data-[hover]:[--btn-icon:theme(colors.purple.200)]"
        ],
        fuchsia: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.fuchsia.500)] [--btn-border:theme(colors.fuchsia.600/90%)]",
            "[--btn-icon:theme(colors.fuchsia.300)] data-[active]:[--btn-icon:theme(colors.fuchsia.200)] data-[hover]:[--btn-icon:theme(colors.fuchsia.200)]"
        ],
        pink: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.pink.500)] [--btn-border:theme(colors.pink.600/90%)]",
            "[--btn-icon:theme(colors.pink.300)] data-[active]:[--btn-icon:theme(colors.pink.200)] data-[hover]:[--btn-icon:theme(colors.pink.200)]"
        ],
        rose: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.rose.500)] [--btn-border:theme(colors.rose.600/90%)]",
            "[--btn-icon:theme(colors.rose.300)] data-[active]:[--btn-icon:theme(colors.rose.200)] data-[hover]:[--btn-icon:theme(colors.rose.200)]"
        ],
        navy: [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.navy.DEFAULT)] [--btn-border:theme(colors.navy.DEFAULT/90%)]",
            "[--btn-icon:theme(colors.navy.50)] data-[active]:[--btn-icon:theme(colors.navy.20)] data-[hover]:[--btn-icon:theme(colors.navy.20)]"
        ],
        "navy-90": [
            "text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.navy.90)] [--btn-border:theme(colors.navy.90/90%)]",
            "[--btn-icon:theme(colors.navy.50)] data-[active]:[--btn-icon:theme(colors.navy.20)] data-[hover]:[--btn-icon:theme(colors.navy.20)]"
        ],
        "navy-light": [
            "text-navy [--btn-bg:theme(colors.navy.5)] [--btn-border:theme(colors.navy.20/80%)] [--btn-hover-overlay:theme(colors.navy.DEFAULT/5%)]",
            "[--btn-icon:theme(colors.navy.DEFAULT)] data-[active]:[--btn-icon:theme(colors.navy.70)] data-[hover]:[--btn-icon:theme(colors.navy.70)]"
        ]
    }
};
export const Button = /*#__PURE__*/ React.forwardRef(function Button({ color, outline, plain, variant, buttonStyle, className, children, ...props }, ref) {
    // For simple/flat/default variants, don't use the complex base styles
    const isLegacyVariant = variant === "flat" || variant === "default" || variant === "simple";
    let classes = clsx(className, !isLegacyVariant && styles.base, outline ? styles.outline : plain ? styles.plain : variant === "flat" ? styles.flat : variant === "default" ? styles.default : variant === "simple" ? clsx(styles.simple.base, buttonStyle === "flat" ? styles.simple.flat : buttonStyle && buttonStyle in styles.simple.colors ? styles.simple.colors[buttonStyle] : color && color in styles.simple.colors ? styles.simple.colors[color] : styles.simple.default) : variant === "sharp" ? clsx(styles.sharp, styles.colors[color ?? "navy-90"]) : clsx(styles.solid, styles.colors[color ?? "dark/zinc"]));
    return "href" in props ? /*#__PURE__*/ _jsx(Link, {
        ...props,
        className: classes,
        ref: ref,
        children: isLegacyVariant ? children : /*#__PURE__*/ _jsx(TouchTarget, {
            children: children
        })
    }) : /*#__PURE__*/ _jsx(HeadlessButton, {
        ...props,
        className: clsx(classes, !isLegacyVariant && "cursor-default"),
        ref: ref,
        children: isLegacyVariant ? children : /*#__PURE__*/ _jsx(TouchTarget, {
            children: children
        })
    });
});
/* Expand the hit area to at least 44×44px on touch devices */ export function TouchTarget({ children }) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            children,
            /*#__PURE__*/ _jsx("span", {
                className: "absolute left-1/2 top-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden",
                "aria-hidden": "true"
            })
        ]
    });
}
